import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    CircularProgress,
    Typography,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetSimulationByIdQuery } from "../../store/services/simulation-service";
import { isLightColor, getTextColor } from '../../utils/colorUtils';

const SimulationContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    cursor: 'default',
}));

const EmailHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
}));

const EmailSubject = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.25rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
}));

const EmailFrom = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    '& .display-name': {
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    '& .email-address': {
        fontStyle: 'italic',
    },
}));

const EmailBody = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    pointerEvents: 'none',
    minHeight: 0,
    fontFamily: theme.typography.fontFamily,

    '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        pointerEvents: 'none',
    },
    '& img': {
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        margin: theme.spacing(1, 0),
    },
    '& button, & input, & select, & textarea': {
        pointerEvents: 'none',
        opacity: 0.7,
    },

    // Force appropriate text colors based on theme - use !important to override inline styles
    '& *': {
        pointerEvents: 'none',
        color: `${theme.palette.text.primary} !important`,
    },

    // Handle elements with potentially problematic background colors
    '& div, & span, & p, & td, & th, & section, & article': {
        // Override transparent/default backgrounds
        backgroundColor: 'transparent !important',
    },

    // Special handling for elements that commonly have white/black backgrounds
    ...(theme.palette.mode === 'dark' && {
        // In dark mode, override white backgrounds that would cause white-on-white text
        '& *[style*="background-color: white"], & *[style*="background-color: #fff"], & *[style*="background-color: #ffffff"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
        '& *[style*="background: white"], & *[style*="background: #fff"], & *[style*="background: #ffffff"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
        // Handle RGB variations
        '& *[style*="background-color: rgb(255, 255, 255)"], & *[style*="background-color: rgba(255, 255, 255"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    }),

    ...(theme.palette.mode === 'light' && {
        // In light mode, override black backgrounds that would cause black-on-black text
        '& *[style*="background-color: black"], & *[style*="background-color: #000"], & *[style*="background-color: #000000"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
        '& *[style*="background: black"], & *[style*="background: #000"], & *[style*="background: #000000"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
        // Handle RGB variations
        '& *[style*="background-color: rgb(0, 0, 0)"], & *[style*="background-color: rgba(0, 0, 0"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    }),

    // Table styling
    '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        backgroundColor: 'transparent !important',
        '& td, & th': {
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1),
            color: `${theme.palette.text.primary} !important`,
            backgroundColor: 'transparent !important',
        },
    },

    // Blockquote styling
    '& blockquote': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        paddingLeft: theme.spacing(2),
        margin: theme.spacing(2, 0),
        fontStyle: 'italic',
        backgroundColor: 'transparent !important',
        color: `${theme.palette.text.primary} !important`,
    },

    // Code styling
    '& pre, & code': {
        backgroundColor: `${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'} !important`,
        padding: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        fontSize: '0.875rem',
        fontFamily: 'monospace',
        color: `${theme.palette.text.primary} !important`,
    },

    // Headings
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        color: `${theme.palette.text.primary} !important`,
        backgroundColor: 'transparent !important',
    },

    // Text formatting
    '& strong, & b': {
        color: `${theme.palette.text.primary} !important`,
    },

    '& em, & i': {
        color: `${theme.palette.text.secondary} !important`,
    },

    // Override any remaining color declarations
    '& *[style*="color"]': {
        color: `${theme.palette.text.primary} !important`,
    },
}));

// Loading and error containers
const StatusContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const ErrorContainer = styled(StatusContainer)(({ theme }) => ({
    color: theme.palette.error.main,
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

// Enhanced wrapper component using your existing color utilities
const ThemedHtmlContent = ({ content, theme }) => {
    const processHtmlContent = (htmlContent) => {
        if (!htmlContent) return '<p>No content available</p>';

        try {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;

            // Remove script tags for security
            const scripts = tempDiv.querySelectorAll('script');
            scripts.forEach(script => script.remove());

            const allElements = tempDiv.querySelectorAll('*');
            allElements.forEach(element => {
                // Remove event handlers
                const attributes = [...element.attributes];
                attributes.forEach(attr => {
                    if (attr.name.startsWith('on')) {
                        element.removeAttribute(attr.name);
                    }
                });

                // Handle links
                if (element.tagName === 'A') {
                    element.removeAttribute('href');
                    element.style.cursor = 'default';
                }

                // Process inline styles for color conflicts using your utilities
                if (element.style) {
                    const bgColor = element.style.backgroundColor || element.style.background;
                    const textColor = element.style.color;

                    // Check for problematic color combinations using your isLightColor utility
                    if (bgColor && textColor) {
                        try {
                            const bgIsLight = isLightColor(bgColor);
                            const textIsLight = isLightColor(textColor);

                            // If both background and text are light/dark, there's a contrast issue
                            if (bgIsLight === textIsLight) {
                                // Remove the problematic styles and let CSS handle it
                                element.style.removeProperty('color');
                                element.style.removeProperty('background-color');
                                element.style.removeProperty('background');
                            }
                        } catch (error) {
                            // If color parsing fails, remove styles to be safe
                            console.warn('Error processing colors:', error);
                            element.style.removeProperty('color');
                            element.style.removeProperty('background-color');
                            element.style.removeProperty('background');
                        }
                    }

                    // Handle specific problematic backgrounds based on theme mode
                    if (bgColor) {
                        try {
                            const bgIsLight = isLightColor(bgColor);

                            // In dark mode, if background is light, it might conflict with white text
                            if (theme.palette.mode === 'dark' && bgIsLight) {
                                // Check if this would be problematic
                                const appropriateTextColor = getTextColor(bgColor);
                                if (appropriateTextColor !== theme.palette.text.primary) {
                                    // Remove background to avoid conflict
                                    element.style.removeProperty('background-color');
                                    element.style.removeProperty('background');
                                }
                            }

                            // In light mode, if background is dark, it might conflict with black text
                            if (theme.palette.mode === 'light' && !bgIsLight) {
                                const appropriateTextColor = getTextColor(bgColor);
                                if (appropriateTextColor !== theme.palette.text.primary) {
                                    element.style.removeProperty('background-color');
                                    element.style.removeProperty('background');
                                }
                            }
                        } catch (error) {
                            // If color processing fails, remove background to be safe
                            console.warn('Error processing background color:', error);
                            element.style.removeProperty('background-color');
                            element.style.removeProperty('background');
                        }
                    }
                }
            });

            return tempDiv.innerHTML;
        } catch (error) {
            console.warn('Error processing HTML content:', error);
            return '<p>Error displaying content</p>';
        }
    };

    return (
        <div
            dangerouslySetInnerHTML={{
                __html: processHtmlContent(content)
            }}
        />
    );
};

ThemedHtmlContent.propTypes = {
    content: PropTypes.string,
    theme: PropTypes.object.isRequired
};

// Main component
const SimulationPreview = ({ simulationId }) => {
    const theme = useTheme();

    const {
        data: simData,
        isLoading: simulationLoading,
        error: simulationError
    } = useGetSimulationByIdQuery({
        simId: simulationId,
        includes: '',
    }, {
        skip: !simulationId
    });

    if (simulationLoading) {
        return (
            <StatusContainer>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" color="textSecondary">
                    Loading simulation...
                </Typography>
            </StatusContainer>
        );
    }

    if (simulationError) {
        return (
            <ErrorContainer>
                <Typography variant="h6" color="error">
                    Error Loading Simulation
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {simulationError.message || simulationError.toString()}
                </Typography>
            </ErrorContainer>
        );
    }

    if (!simData) {
        return (
            <StatusContainer>
                <Typography variant="body2" color="textSecondary">
                    No simulation data available
                </Typography>
            </StatusContainer>
        );
    }

    return (
        <SimulationContainer>
            <EmailHeader>
                <EmailSubject variant="h5">
                    {simData.emailSubject || "No Subject"}
                </EmailSubject>
                <EmailFrom variant="body2">
                    <span className="display-name">
                        {simData.emailDisplayName || "Unknown Sender"}
                    </span>
                    {simData.emailFromAddress && (
                        <span className="email-address">
                            {' '}&lt;{simData.emailFromAddress}&gt;
                        </span>
                    )}
                </EmailFrom>
            </EmailHeader>
            <EmailBody>
                <ThemedHtmlContent content={simData.content} theme={theme} />
            </EmailBody>
        </SimulationContainer>
    );
};

SimulationPreview.propTypes = {
    simulationId: PropTypes.string.isRequired
};

export default SimulationPreview;